<template>
  <article class="print license-application-print" aria-hidden="true">
    <header class="application-header">
      <div class="municipality-name">{{ municipalityName }}</div>
      <h1>{{ applicationTitle }}</h1>
      <div class="application-meta">
        <div>
          <span class="meta-label">Application #</span>
          <span>{{ applicationNumber }}</span>
        </div>
        <div>
          <span class="meta-label">Date applied</span>
          <span>{{ appliedDate }}</span>
        </div>
      </div>
    </header>

    <section class="application-section">
      <h2>Property</h2>
      <div class="field-grid">
        <div class="print-field field-wide">
          <div class="field-label">Property address</div>
          <div class="field-value">{{ propertyAddress }}</div>
        </div>
        <div class="print-field">
          <div class="field-label">Parcel ID</div>
          <div class="field-value">{{ printableValue(license.propertyId) }}</div>
        </div>
        <div class="print-field">
          <div class="field-label">Property record owner(s)</div>
          <div class="field-value">{{ propertyOwners }}</div>
        </div>
      </div>
    </section>

    <section class="application-section">
      <h2>Contact information</h2>
      <div class="field-grid">
        <div class="print-field">
          <div class="field-label">Name(s)</div>
          <div class="field-value">{{ contactNames }}</div>
        </div>
        <div class="print-field">
          <div class="field-label">Mailing address</div>
          <div class="field-value">{{ printableValue(license.contact?.address) }}</div>
        </div>
        <div class="print-field">
          <div class="field-label">Email</div>
          <div class="field-value">{{ printableValue(license.contact?.email) }}</div>
        </div>
        <div class="print-field">
          <div class="field-label">Phone number</div>
          <div class="field-value">{{ contactPhone }}</div>
        </div>
      </div>
    </section>

    <section class="application-section">
      <h2>Rental listings</h2>
      <div v-if="license.listings?.length" class="listing-list">
        <div v-for="(listing, index) in license.listings" :key="`${listing}-${index}`" class="listing-url">
          {{ listing }}
        </div>
      </div>
      <div v-else class="field-value">None provided</div>
    </section>

    <section v-if="customSections.length" class="application-section custom-form-section">
      <h2>Municipality-specific information</h2>
      <p v-if="formDescription" class="form-description">{{ formDescription }}</p>

      <div v-for="section in customSections" :key="section.key" class="custom-page">
        <h3 v-if="section.title">{{ section.title }}</h3>
        <p v-if="section.description" class="page-description">{{ section.description }}</p>

        <template v-for="item in section.items" :key="item.key">
          <div v-if="item.kind === 'heading'" class="custom-heading">
            <h4>{{ item.label }}</h4>
            <p v-if="item.description">{{ item.description }}</p>
          </div>
          <p v-else-if="item.kind === 'note'" class="custom-note">{{ item.value }}</p>
          <div v-else class="print-field custom-answer">
            <div class="field-label">{{ item.label }}</div>
            <div v-if="item.description" class="field-description">{{ item.description }}</div>
            <div class="field-value">{{ item.value }}</div>
          </div>
        </template>
      </div>
    </section>
  </article>
</template>

<script>
import { toMoment } from '@strenforcement/domain/dateUtils';
import { License } from '@/models/License';
import { LicenseApplicationForm } from '@/models/LicenseApplicationForm';
import SurveyModel from '@/models/SurveyModel';
import { buildPrintableSurveySections, formatPrintableValue } from '@/licenseApplicationPrint';

export default {
  props: {
    license: {
      type: License,
      required: true
    }
  },
  computed: {
    applicationForm() {
      return new LicenseApplicationForm(this.license.city);
    },
    applicationTitle() {
      return this.applicationForm.title || 'License Registration Application';
    },
    municipalityName() {
      return this.license.city?.formalName || this.license.city?.name || 'Municipality';
    },
    applicationNumber() {
      return this.license.number || this.license.id || 'Not provided';
    },
    appliedDate() {
      const applied = toMoment(this.license.appliedOn);
      return applied ? applied.format('MMMM D, YYYY') : 'Not provided';
    },
    propertyAddress() {
      const addressLines = this.license.property?.addressLines || [];
      return formatPrintableValue(
        addressLines.filter(Boolean).join('\n')
          || this.license.addressFormatted
          || this.license.unverifiedAddress
      );
    },
    propertyOwners() {
      return formatPrintableValue(this.license.property?.owners || []);
    },
    contactNames() {
      return formatPrintableValue(this.license.contact?.owners || []);
    },
    contactPhone() {
      const digits = String(this.license.contact?.phone || '').replace(/[^\d]/g, '');
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      return formatPrintableValue(this.license.contact?.phone);
    },
    formDescription() {
      return this.applicationForm.description || '';
    },
    customSurvey() {
      return SurveyModel.createFromLicense(this.license);
    },
    customSections() {
      return buildPrintableSurveySections(
        this.customSurvey,
        this.license.data?.customFieldValues || []
      );
    }
  },
  methods: {
    printableValue(value) {
      return formatPrintableValue(value);
    }
  }
};
</script>

<style scoped>
.license-application-print {
  box-sizing: border-box;
  width: 100%;
  color: #111;
  background: #fff;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 10pt;
  line-height: 1.4;
}

.application-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #222;
}

.municipality-name {
  font-size: 11pt;
  font-weight: 600;
}

h1 {
  margin: 0.15rem 0 0.75rem;
  font-size: 20pt;
  line-height: 1.15;
}

.application-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 2rem;
}

.application-meta > div {
  display: flex;
  gap: 0.4rem;
}

.meta-label,
.field-label {
  font-weight: 700;
}

.application-section {
  padding: 0.9rem 0;
  border-bottom: 1px solid #bbb;
}

.application-section:last-child {
  border-bottom: 0;
}

h2,
h3,
h4,
p {
  margin-top: 0;
}

h2 {
  margin-bottom: 0.55rem;
  font-size: 13pt;
}

h3 {
  margin-bottom: 0.5rem;
  font-size: 11pt;
}

h4 {
  margin-bottom: 0.15rem;
  font-size: 10.5pt;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem 1.5rem;
}

.field-wide {
  grid-column: 1 / -1;
}

.print-field,
.custom-heading,
.custom-note,
.listing-url {
  break-inside: avoid;
  page-break-inside: avoid;
}

.field-value {
  margin-top: 0.1rem;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.field-description,
.page-description,
.form-description,
.custom-heading p {
  margin-bottom: 0.25rem;
  color: #444;
  font-size: 9pt;
  white-space: pre-wrap;
}

.listing-list {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.listing-url {
  overflow-wrap: anywhere;
}

.custom-page {
  margin-top: 0.8rem;
}

.custom-page:first-of-type {
  margin-top: 0;
}

.custom-answer {
  margin: 0 0 0.7rem;
  padding-bottom: 0.7rem;
  border-bottom: 1px solid #ddd;
}

.custom-answer:last-child {
  border-bottom: 0;
}

.custom-heading {
  margin: 0.85rem 0 0.4rem;
}

.custom-note {
  margin-bottom: 0.7rem;
  white-space: pre-wrap;
}

@media print {
  @page {
    size: auto;
    margin: 0.5in 0.65in;
  }

  .application-section,
  .custom-page {
    break-before: auto;
  }
}
</style>

